import React from 'react';

import i18n from '@/i18n';
import Icon from '@/shared/ui/icon/Icon';
import AppSelect from '@/shared/ui/select/AppSelect';

import styles from './import.module.scss';

export const IGNORE = '__ignore__';
export const CONSTANT = '__constant__';

/**
 * Secondo passo di un import: scelta del foglio e associazione campo → colonna.
 *
 * Il componente è controllato: `mapping` e `onChange` stanno nel chiamante, che
 * resta padrone di quando ricalcolare l'anteprima.
 *
 * @param {Object} props
 * @param {import('./importSchema').ImportField[]} props.schema
 * @param {{ columns: string[], rows: Object[], sheets?: string[], sheetName?: string }} props.parsed
 * @param {Record<string, {source?: string, constant?: *}>} props.mapping
 * @param {(key: string, rule: {source?: string, constant?: *}) => void} props.onChange
 * @param {(sheetName: string) => void} [props.onSheetChange]
 * @param {boolean} [props.showSheetSelect]
 */
export default function ImportMappingStep({
  schema,
  parsed,
  mapping,
  onChange,
  onSheetChange,
  showSheetSelect = false,
}) {
  const sourceOptions = [
    { value: IGNORE, label: i18n.t('import_ignora', { defaultValue: '— Ignora —' }) },
    { value: CONSTANT, label: i18n.t('import_valore_fisso', { defaultValue: 'Valore fisso…' }) },
    ...(parsed?.columns || []).map((c) => ({ value: c, label: c })),
  ];

  const selectedOption = (key) => {
    const rule = mapping?.[key] || {};
    if (rule.source !== undefined) return { value: rule.source, label: rule.source };
    if (rule.constant !== undefined) {
      return {
        value: CONSTANT,
        label: i18n.t('import_valore_fisso', { defaultValue: 'Valore fisso…' }),
      };
    }
    return sourceOptions[0];
  };

  const setSource = (key, option) => {
    const value = option?.value;
    if (value === IGNORE || value === undefined) return onChange(key, {});
    if (value === CONSTANT) {
      const field = schema.find((f) => f.key === key);
      return onChange(key, { constant: field?.constant ?? '' });
    }
    return onChange(key, { source: value });
  };

  return (
    <>
      {showSheetSelect && (parsed?.sheets?.length || 0) > 1 && (
        <div className="field">
          <label htmlFor="import-sheet">
            {i18n.t('import_foglio', { defaultValue: 'Foglio' })}
          </label>
          <AppSelect
            inputId="import-sheet"
            value={{ value: parsed.sheetName, label: parsed.sheetName }}
            options={parsed.sheets.map((s) => ({ value: s, label: s }))}
            onChange={(opt) => opt?.value && onSheetChange?.(opt.value)}
          />
        </div>
      )}

      <p className={styles.hint}>
        <Icon name="info" />
        {i18n.t('import_righe_lette', {
          defaultValue: '{{count}} righe lette dal file',
          count: parsed?.rows?.length ?? 0,
        })}
      </p>

      <div className={styles.mappingList}>
        {schema.map((field) => {
          const rule = mapping?.[field.key] || {};
          return (
            <div key={field.key} className={styles.mappingRow}>
              <span className={styles.fieldLabel}>
                {field.label}
                {field.required && <span className={styles.required}> *</span>}
              </span>
              <AppSelect
                inputId={`map-${field.key}`}
                value={selectedOption(field.key)}
                options={sourceOptions}
                onChange={(opt) => setSource(field.key, opt)}
              />
              {rule.constant !== undefined && (
                <input
                  className="form-control app-input"
                  value={rule.constant ?? ''}
                  placeholder={i18n.t('import_valore_fisso', { defaultValue: 'Valore fisso' })}
                  onChange={(e) => onChange(field.key, { constant: e.target.value })}
                />
              )}
            </div>
          );
        })}
      </div>
    </>
  );
}
